import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import ActionsMenu from "@/components/dashboard/ActionsMenu";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import useCategories from "@/hooks/useCategories";
import useCategoryMuations from "@/hooks/useCategoryMutations";
import { Row } from "@tanstack/react-table";
import React from "react";

function Category() {
  const { data, isLoading } = useCategories();
  const [name, setName] = React.useState("");
  const [openAdd, setOpenAdd] = React.useState(false);
  const [openEdit, setOpenEdit] = React.useState(false);
  const [openConfirm, setOpenConfirm] = React.useState(false);
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const confirmActionButtonRef = React.useRef<() => void>(() => {});

  const { createtCategoryMutation, editCategoryMutation, deleteCategoryMutation } =
    useCategoryMuations({
      resetForm: () => setName(""),
      closeAdd: () => setOpenAdd(false),
      closeEdit: () => setOpenEdit(false),
      clearEditingId: () => setEditingId(null),
      closeConfirm: () => setOpenConfirm(false),
    });

  function startEdit(row: { id: number; name: string }) {
    setEditingId(row.id);
    setName(row.name);
    setOpenEdit(true);
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    createtCategoryMutation.mutate({ name: name.trim() });
  }

  function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !editingId) return;
    editCategoryMutation.mutate({ id: editingId, name: name.trim() });
  }

  function triggerDelete() {
    if (editingId) {
      const idToDelete = editingId;
      setEditingId(null); // Clear immediately to prevent double calls
      deleteCategoryMutation.mutate(idToDelete);
    }
  }

  const columns = [
    { accessorKey: "id", header: "ID" },
    { accessorKey: "name", header: "Category Name" },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: { row: Row<{ id: number; name: string }> }) => (
        <ActionsMenu
          onEdit={() => startEdit(row.original)}
          onDelete={() => {
            setEditingId(row.original.id);
            setOpenConfirm(true);
          }}
        />
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <h1 className="text-3xl">Feed Categories</h1>
      <div className="flex items-center justify-between">
        <p>Manage all feed categories in one place.</p>
        <Dialog open={openAdd} onOpenChange={setOpenAdd}>
          <DialogTrigger asChild>
            <Button>+ Add Feed Category</Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleCreate} className="space-y-3">
              <DialogHeader>
                <DialogTitle>Add Feed Category</DialogTitle>
                <DialogDescription>
                  Enter the name of the new category.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-2">
                <label htmlFor="categoryName">Feed Category Name</label>
                <Input
                  id="categoryName"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant={"outline"}>Cancel</Button>
                </DialogClose>
                <Button
                  type="submit"
                  disabled={createtCategoryMutation.isPending || !name.trim()}
                >
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <DataTable
        columns={columns as any}
        data={(data as any) || []}
        placeholder="Filter Category"
        filterColumns={["name"]}
      />
      <ConfirmDialog
        open={openConfirm}
        onOpenChange={(o) => {
          if (!o) {
            setEditingId(null);
          }
          setOpenConfirm(o);
        }}
        isLoading={deleteCategoryMutation.isPending}
        handleConfirm={() => triggerDelete()}
        AlertdialogDescription="This will permanently delete this feed category. Are you sure?"
      />
      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Feed Category</DialogTitle>
            <DialogDescription>
              Update the name of the category.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-3">
            <div className="grid gap-2">
              <label htmlFor="categoryNameEdit">Feed Category Name</label>
              <Input
                id="categoryNameEdit"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant={"outline"}>Cancel</Button>
              </DialogClose>
              <Button
                type="submit"
                disabled={editCategoryMutation.isPending || !name.trim()}
              >
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Category;
